import * as React from "react";

import { useNavigate } from "@opendash/router";
import { getTitle, useAppState } from "../state";

import { useTranslation } from "@opendash/i18n";
import { Icon } from "@opendash/icons";
import styled from "styled-components";
import { AppLayout } from "./AppLayout";
import { useTexts } from "../hooks/useTexts";

const OuterContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  @media only screen and (max-width: 800px) {
    padding-top: 7px;
    padding-bottom: 5px;
  }
`;

const CircularContainer = styled.div`
  position: relative;
  width: 720px;
  height: 720px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media only screen and (max-width: 1000px) {
    width: 640px;
    height: 640px;
  }

  @media only screen and (max-width: 700px) {
    width: 100%;
    height: auto;
    flex-direction: column;
    gap: 15px;
  }
`;
const CircularButton = styled.div`
  position: absolute;
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ffffff 0%, #f0f8ff 100%);
  border: 3px solid var(--primaryColor);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  text-align: center;
  padding: 18px;

  &:hover {
    box-shadow: 0 15px 35px rgba(78, 225, 255, 0.3);
    border-color: #4ee1ff;
    background: linear-gradient(135deg, #f0f8ff 0%, #e6f7ff 100%);
  }

  /* Positioning for 6 elements in circle (360°/6 = 60°) */
  &:nth-child(2) {
    transform: rotate(0deg) translateY(-280px) rotate(0deg);

    &:hover {
      transform: rotate(0deg) translateY(-280px) rotate(0deg) scale(1.1);
    }
  }

  &:nth-child(3) {
    transform: rotate(60deg) translateY(-280px) rotate(-60deg);

    &:hover {
      transform: rotate(60deg) translateY(-280px) rotate(-60deg) scale(1.1);
    }
  }

  &:nth-child(4) {
    transform: rotate(120deg) translateY(-280px) rotate(-120deg);

    &:hover {
      transform: rotate(120deg) translateY(-280px) rotate(-120deg) scale(1.1);
    }
  }

  &:nth-child(5) {
    transform: rotate(180deg) translateY(-280px) rotate(-180deg);

    &:hover {
      transform: rotate(180deg) translateY(-280px) rotate(-180deg) scale(1.1);
    }
  }

  &:nth-child(6) {
    transform: rotate(240deg) translateY(-280px) rotate(-240deg);

    &:hover {
      transform: rotate(240deg) translateY(-280px) rotate(-240deg) scale(1.1);
    }
  }

  &:nth-child(7) {
    transform: rotate(300deg) translateY(-280px) rotate(-300deg);

    &:hover {
      transform: rotate(300deg) translateY(-280px) rotate(-300deg) scale(1.1);
    }
  }

  @media only screen and (max-width: 1000px) {
    width: 160px;
    height: 160px;
    padding: 15px;

    /* Icons ausblenden bei mittlerer Bildschirmgröße */
    .button-icon {
      display: none;
    }

    &:nth-child(2) {
      transform: rotate(0deg) translateY(-240px) rotate(0deg);

      &:hover {
        transform: rotate(0deg) translateY(-240px) rotate(0deg) scale(1.1);
      }
    }

    &:nth-child(3) {
      transform: rotate(60deg) translateY(-240px) rotate(-60deg);

      &:hover {
        transform: rotate(60deg) translateY(-240px) rotate(-60deg) scale(1.1);
      }
    }

    &:nth-child(4) {
      transform: rotate(120deg) translateY(-240px) rotate(-120deg);

      &:hover {
        transform: rotate(120deg) translateY(-240px) rotate(-120deg) scale(1.1);
      }
    }

    &:nth-child(5) {
      transform: rotate(180deg) translateY(-240px) rotate(-180deg);

      &:hover {
        transform: rotate(180deg) translateY(-240px) rotate(-180deg) scale(1.1);
      }
    }

    &:nth-child(6) {
      transform: rotate(240deg) translateY(-240px) rotate(-240deg);

      &:hover {
        transform: rotate(240deg) translateY(-240px) rotate(-240deg) scale(1.1);
      }
    }

    &:nth-child(7) {
      transform: rotate(300deg) translateY(-240px) rotate(-300deg);

      &:hover {
        transform: rotate(300deg) translateY(-240px) rotate(-300deg) scale(1.1);
      }
    }
  }

  @media only screen and (max-width: 700px) {
    position: relative;
    transform: none !important;
    width: 90%;
    max-width: 280px;
    height: 100px;
    border-radius: 15px;
    margin: 0 auto;
    flex-direction: row;
    padding: 15px;
    gap: 15px;

    /* Icons wieder anzeigen auf Mobile */
    .button-icon {
      display: block;
    }
  }
`;

const CenterWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  z-index: 10;

  @media only screen and (max-width: 700px) {
    position: relative;
    top: auto;
    left: auto;
    transform: none;
    width: 90%;
    max-width: 280px;
    margin: 0 auto 20px auto;
    gap: 8px;
  }
`;

const CenterHalf = styled.div<{ position: "top" | "bottom" }>`
  width: 180px;
  height: 86px;
  background: var(--primaryColor);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  text-align: center;
  padding: 10px 18px;
  gap: 8px;
  border-radius: ${(props) =>
    props.position === "top" ? "90px 90px 6px 6px" : "6px 6px 90px 90px"};

  &:hover {
    box-shadow: 0 10px 30px rgba(78, 225, 255, 0.4);
    background: #4ee1ff;
  }

  .center-icon {
    display: none;
  }

  @media only screen and (max-width: 1000px) {
    width: 160px;
    height: 76px;
    padding: 8px 14px;
  }

  @media only screen and (max-width: 700px) {
    width: 100%;
    height: 50px;
    border-radius: 15px !important;
    flex-direction: row;

    .center-icon {
      display: block;
    }
  }
`;

const ButtonTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  margin: 8px 0 4px 0;
  line-height: 1.1;

  @media only screen and (max-width: 1000px) {
    font-size: 13px;
    margin: 6px 0 2px 0;
  }

  @media only screen and (max-width: 700px) {
    font-size: 16px;
    margin: 0;
    flex: 1;
    text-align: left;
  }
`;

const ButtonSubtitle = styled.p`
  font-size: 11px;
  color: #666;
  margin: 0;
  line-height: 1.2;

  @media only screen and (max-width: 1000px) {
    font-size: 10px;
  }

  @media only screen and (max-width: 700px) {
    display: none;
  }
`;

export const FrontpageRoute = React.memo(function FrontpageRoute() {
  const app = useAppState();
  const navigate = useNavigate();
  const i18n = useTranslation();
  const { t } = useTexts();

  const { tenantConfig } = useAppState();

  const background = app.frontpageImage;

  // Zentraler Button oben (Wasserkreislauf)
  const centerSection = {
    key: "CENTER",
    title: t("frontpage.button.wasserkreislauf", "Der Wasserkreislauf"),
    subtitle: "Ein Überblick",
    icon: "fa:tint",
    path: "/wasserklaerer/wasserkreislauf",
  };

  // Zentraler Button unten (FAQ)
  const faqSection = {
    key: "FAQ",
    title: t("frontpage.button.faq", "Fragen und Antworten"),
    icon: "fa:question-circle",
    path: "/wasserklaerer/faq",
  };

  // Äußere Buttons (6 Stück im Kreis)
  const outerSections = [
    {
      key: "A",
      title: t(
        "frontpage.button.wasserangebot",
        "Wasserangebot und Wasserverbrauch in Deutschland"
      ),
      subtitle: "Grundlagen",
      icon: "fa:tint",
      path: "/wasserklaerer/wasserangebot/angebot",
    },
    {
      key: "B",
      title: t("frontpage.button.wasserqualitaet", "Wasseraufbereitung"),
      subtitle: "Was passiert bevor das Wasser bei dir ankommt?",
      icon: "fa:filter",
      path: "/wasserklaerer/wasseraufbereitung/wasserwerk",
    },
    {
      key: "C",
      title: "Wasser im privaten Haushalt",
      subtitle: "Alles rund um das Thema Wasser bei dir zuhause",
      icon: "fa:home",
      path: "/wasserklaerer/privathaushalt/verbrauch",
    },
    {
      key: "E",
      title: t("frontpage.button.regenwasser", "Regen- und Grauwassernutzung"),
      subtitle: "Nachhaltige Wassernutzung im Haushalt und Garten",
      icon: "fa:recycle",
      path: "/wasserklaerer/regenwasser",
    },
    {
      key: "E",
      title: t("frontpage.button.abwasser", "Abwasser und Umwelt"),
      subtitle: "Die Reise des Wassers durch den Abfluss",
      icon: "fa:recycle",
      path: "/wasserklaerer/abwasser/detail",
    },
    {
      key: "G",
      title: t("frontpage.button.wasserknappheit", "Wasserknappheit"),
      subtitle: "Grundlagen, Entstehung und Wasserfußabdruck",
      icon: "fa:tint-slash",
      path: "/wasserklaerer/wasserknappheit",
    },
  ];

  return (
    <AppLayout fullWidth>
      <OuterContainer
        style={
          background
            ? {
                backgroundImage: `url("${background}")`,
              }
            : undefined
        }
      >
        <CircularContainer>
          <CenterWrapper>
            <CenterHalf
              position="top"
              onClick={() => {
                navigate(centerSection.path);
              }}
            >
              <Icon
                icon={centerSection.icon}
                className="center-icon"
                style={{
                  fontSize: "24px",
                  color: "white",
                  flexShrink: 0,
                }}
              />
              <div
                style={{
                  color: "white",
                  fontSize: "13px",
                  fontWeight: "bold",
                  lineHeight: "1.2",
                  textAlign: "center",
                }}
              >
                Der
                <br />
                Wasserkreislauf
              </div>
            </CenterHalf>
            <CenterHalf
              position="bottom"
              onClick={() => {
                navigate(faqSection.path);
              }}
            >
              <Icon
                icon={faqSection.icon}
                className="center-icon"
                style={{
                  fontSize: "24px",
                  color: "white",
                  flexShrink: 0,
                }}
              />
              <div
                style={{
                  color: "white",
                  fontSize: "13px",
                  fontWeight: "bold",
                  lineHeight: "1.2",
                }}
              >
                {faqSection.title}
              </div>
            </CenterHalf>
          </CenterWrapper>

          {outerSections.map((section, index) => (
            <CircularButton
              key={section.key}
              onClick={() => {
                navigate(section.path);
              }}
            >
              <Icon
                className="button-icon"
                style={{
                  fontSize: "32px",
                  color: "var(--primaryColor)",
                  marginBottom: "8px",
                  flexShrink: 0,
                }}
                icon={section.icon}
              />
              <ButtonTitle>{section.title}</ButtonTitle>
              <ButtonSubtitle>{section.subtitle}</ButtonSubtitle>
            </CircularButton>
          ))}
        </CircularContainer>
      </OuterContainer>
    </AppLayout>
  );
});
